import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  TextField,
  Autocomplete,
  InputAdornment,
  Skeleton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import { Link } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { sendMetricsReportsToManagers, getAllTasks, fetchUsers, getTasksByWeek, getTasksByMonth } from "../api/services";

// ---------------------------------------------------------------
// Shared look (design tokens + small UI helpers) - same as Earnings
// ---------------------------------------------------------------
const ui = {
  primary: "#009db9",
  primaryDark: "#007a8a",
  primarySoft: "#e6f6f9",
  text: "#1f2933",
  muted: "#6b7280",
  border: "#e5e7eb",
  surface: "#f7f9fb",
  success: "#2e7d32",
  successSoft: "#e8f5e9",
  danger: "#d32f2f",
  dangerSoft: "#ffebee",
  warning: "#b26a00",
  warningSoft: "#fff4e5",
  radius: "16px",
  shadow: "0 4px 20px rgba(16, 24, 40, 0.06)",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#fff",
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: ui.primary },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: ui.primary },
    "&.Mui-disabled": { backgroundColor: "#f3f5f7" },
  },
  "& label.Mui-focused": { color: ui.primary },
};

const primaryButtonSx = {
  backgroundColor: ui.primary,
  color: "#fff",
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 700,
  px: 3,
  py: 1.25,
  boxShadow: "none",
  "&:hover": { backgroundColor: ui.primaryDark, boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: "#d5dbe0", color: "#8a949e" },
};

const secondaryButtonSx = {
  textTransform: "none",
  fontWeight: 600,
  borderRadius: "10px",
  color: ui.primary,
  borderColor: ui.primary,
  px: 2.5,
  "&:hover": { borderColor: ui.primaryDark, backgroundColor: ui.primarySoft },
  "&.Mui-disabled": { color: "#b0b7be", borderColor: "#e0e4e8" },
};

const headCellSx = {
  fontWeight: 700,
  color: ui.primaryDark,
  backgroundColor: ui.primarySoft,
  fontSize: "0.8rem",
  py: 1.5,
  borderBottom: `1px solid ${ui.border}`,
};

const bodyCellSx = {
  color: ui.text,
  py: 1.75,
  fontSize: "0.875rem",
  borderBottom: `1px solid ${ui.border}`,
};

const mobileCardSx = {
  border: `1px solid ${ui.border}`,
  borderRadius: "12px",
  p: 2,
  backgroundColor: "#fff",
};

const BackButton = () => (
  <Box sx={{ mt: { xs: 4, sm: 5 }, mb: 2 }}>
    <Button
      component={Link}
      to="/manager-team-metrics"
      startIcon={<ArrowBackIcon fontSize="small" />}
      variant="outlined"
      size="small"
      sx={secondaryButtonSx}
    >
      Back to Team Administration
    </Button>
  </Box>
);

const PageHeader = ({ title }) => (
  <Box sx={{ mb: { xs: 3, md: 4 } }}>
    <Typography
      variant="h4"
      sx={{
        fontWeight: "bold",
        color: ui.text,
        letterSpacing: 0.5,
        fontSize: { xs: "1.5rem", sm: "1.85rem", md: "2.125rem" },
        position: "relative",
        "&::after": {
          content: '""',
          display: "block",
          backgroundColor: ui.primary,
          height: "4px",
          width: { xs: "80px", sm: "100px" },
          position: "absolute",
          left: 0,
          bottom: "-8px",
        },
      }}
    >
      {title}
    </Typography>
  </Box>
);

const SectionCard = ({ title, action, children }) => (
  <Card
    sx={{
      mb: { xs: 2.5, md: 4 },
      borderRadius: ui.radius,
      border: `1px solid ${ui.border}`,
      backgroundColor: "#fff",
      boxShadow: ui.shadow,
      overflow: "visible",
    }}
  >
    <CardContent
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        "&:last-child": { pb: { xs: 2, sm: 3, md: 4 } },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          gap: 2,
          mb: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: ui.text,
            pl: 1.5,
            borderLeft: `4px solid ${ui.primary}`,
            lineHeight: 1.3,
          }}
        >
          {title}
        </Typography>
        {action}
      </Box>
      {children}
    </CardContent>
  </Card>
);

const StatGrid = ({ children }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
      gap: { xs: 1.5, md: 2 },
      mb: { xs: 2.5, md: 4 },
    }}
  >
    {children}
  </Box>
);

const statTones = {
  primary: { bg: ui.primarySoft, color: ui.primary },
  success: { bg: ui.successSoft, color: ui.success },
  warning: { bg: ui.warningSoft, color: ui.warning },
  danger: { bg: ui.dangerSoft, color: ui.danger },
};

const StatCard = ({ label, value, icon: Icon, tone = "primary" }) => {
  const t = statTones[tone] || statTones.primary;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 2,
        minWidth: 0,
        borderRadius: "14px",
        border: `1px solid ${ui.border}`,
        backgroundColor: "#fff",
        boxShadow: ui.shadow,
      }}
    >
      {Icon && (
        <Box
          sx={{
            flexShrink: 0,
            width: 40,
            height: 40,
            borderRadius: "10px",
            backgroundColor: t.bg,
            color: t.color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon fontSize="small" />
        </Box>
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ color: ui.muted }} noWrap>
          {label}
        </Typography>
        <Typography
          sx={{
            fontWeight: 700,
            color: ui.text,
            fontSize: { xs: "1.05rem", md: "1.2rem" },
            lineHeight: 1.3,
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

const chipTones = {
  success: { bg: ui.successSoft, color: ui.success },
  danger: { bg: ui.dangerSoft, color: ui.danger },
  warning: { bg: ui.warningSoft, color: ui.warning },
  info: { bg: ui.primarySoft, color: ui.primaryDark },
  neutral: { bg: "#eef1f4", color: "#4b5563" },
};

const PillChip = ({ label, tone = "neutral" }) => {
  const t = chipTones[tone] || chipTones.neutral;
  return (
    <Chip
      size="small"
      label={label}
      sx={{ fontWeight: 700, borderRadius: "8px", backgroundColor: t.bg, color: t.color }}
    />
  );
};

const EmptyState = ({ icon: Icon = AssignmentOutlinedIcon, title, message }) => (
  <Box sx={{ textAlign: "center", py: { xs: 5, md: 7 }, px: 2 }}>
    <Box
      sx={{
        width: 64,
        height: 64,
        mx: "auto",
        mb: 2,
        borderRadius: "50%",
        backgroundColor: ui.primarySoft,
        color: ui.primary,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Icon sx={{ fontSize: 32 }} />
    </Box>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: ui.text }}>
      {title}
    </Typography>
    {message && (
      <Typography variant="body2" sx={{ color: ui.muted, mt: 0.5, maxWidth: 360, mx: "auto" }}>
        {message}
      </Typography>
    )}
  </Box>
);

const ListSkeleton = ({ rows = 5 }) => (
  <Box role="status" aria-label="Loading" sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
    {Array.from({ length: rows }).map((_, i) => (
      <Skeleton key={i} variant="rounded" height={56} animation="wave" />
    ))}
  </Box>
);

const PagerBar = ({ currentPage, totalPages, onPrev, onNext }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      mt: 3,
      gap: { xs: 1, sm: 2 },
      flexWrap: "wrap",
    }}
  >
    <Button
      variant="outlined"
      onClick={onPrev}
      disabled={currentPage === 0}
      sx={secondaryButtonSx}
    >
      Previous
    </Button>
    <Typography sx={{ color: ui.text, fontSize: { xs: "0.875rem", sm: "0.95rem" } }}>
      Page {currentPage + 1} of {totalPages}
    </Typography>
    <Button
      variant="outlined"
      onClick={onNext}
      disabled={currentPage >= totalPages - 1}
      sx={secondaryButtonSx}
    >
      Next
    </Button>
  </Box>
);

// MOVED OUTSIDE COMPONENT - This prevents recreating array on every render
const monthNames = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const ViewMetrics = () => {
  const [weekFilter, setWeekFilter] = useState("All Weeks");
  const [monthFilter, setMonthFilter] = useState("All Months");
  const [employeeFilter, setEmployeeFilter] = useState("All Employees");
  const [loading, setLoading] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [weeks, setWeeks] = useState(["All Weeks"]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const pageSize = 10;

  const months = ["All Months", ...monthNames];

  useEffect(() => {
    const fetchData = async () => {
      setLoadingTasks(true);
      try {
        const usersResponse = await fetchUsers(0, 100);
        const activeUsers = usersResponse?.[0]?.activeUsers || [];
        const validUsers = activeUsers.filter(
          (user) => user && user.id && typeof user.id === "string" && user.name
        );
        setUsers(validUsers);

        const tasksResponse = await getAllTasks(currentPage, pageSize);
        const taskArray = tasksResponse.data.content || [];
        setTotalPages(tasksResponse.data.totalPages || 1);

        if (Array.isArray(taskArray)) {
          const formattedTasks = taskArray.map((task) => {
            return {
              id: task.id,
              employeeId: task.employee.id,
              employee: task.employee.name,
              week: `Week ${task.week}`,
              weekNumber: task.week,
              storyPoints: task.actualPoints ?? task.storyPoints,
              bonusStatus: task.bonusStatus,
              progress: task.completed ? 100 : 0,
              month: new Date(task.createdDate).getMonth() + 1,
              monthName: new Date(task.createdDate).toLocaleString("default", { month: "long" }),
            };
          });
          setTasks(formattedTasks);
        } else {
          setTasks([]);
          setSnackbarState({
            open: true,
            message: "No tasks found in response.",
            severity: "warning",
          });
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setSnackbarState({
          open: true,
          message: "Failed to load data. Please try again.",
          severity: "error",
        });
        setTasks([]);
        setWeeks(["All Weeks"]);
      } finally {
        setLoadingTasks(false);
      }
    };

    fetchData();
  }, [currentPage, pageSize]);

  // FIXED: Removed weekFilter from dependencies to prevent infinite loop
  useEffect(() => {
    if (monthFilter === "All Months") {
      setWeeks(["All Weeks"]);
    } else {
      const monthIndex = monthNames.indexOf(monthFilter) + 1;
      const year = 2025;
      const daysInMonth = new Date(year, monthIndex, 0).getDate();
      const numWeeks = Math.ceil(daysInMonth / 7);
      const weekOptions = ["All Weeks"];
      for (let i = 1; i <= numWeeks; i++) {
        weekOptions.push(`Week ${i}`);
      }
      setWeeks(weekOptions);
      if (weekFilter !== "All Weeks" && !weekOptions.includes(weekFilter)) {
        setWeekFilter("All Weeks");
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthFilter]);

  const fetchTasks = useCallback(async () => {
    setLoadingTasks(true);
    try {
      let tasksResponse;
      let taskArray = [];

      if (employeeFilter !== "All Employees" && monthFilter !== "All Months") {
        const user = users.find((u) => u.name === employeeFilter);
        const userId = user ? user.id : null;
        const monthIndex = monthNames.indexOf(monthFilter) + 1;

        if (!userId) {
          setSnackbarState({
            open: true,
            message: "Selected employee not found.",
            severity: "error",
          });
          setTasks([]);
          return;
        }

        if (weekFilter !== "All Weeks") {
          const weekNumber = parseInt(weekFilter.replace("Week ", ""));
          tasksResponse = await getTasksByWeek(userId, monthIndex, weekNumber);
          taskArray = tasksResponse.data || [];
        } else {
          tasksResponse = await getTasksByMonth(userId, 2025, monthIndex);
          taskArray = tasksResponse.data || [];
        }
      } else {
        tasksResponse = await getAllTasks(currentPage, pageSize);
        taskArray = tasksResponse.data.content || [];
        setTotalPages(tasksResponse.data.totalPages || 1);
      }

      if (Array.isArray(taskArray)) {
        const formattedTasks = taskArray.map((task) => {
          return {
            id: task.id,
            employeeId: task.employee.id,
            employee: task.employee.name,
            week: `Week ${task.week}`,
            weekNumber: task.week,
            storyPoints: task.actualPoints ?? task.storyPoints,
            bonusStatus: task.bonusStatus,
            progress: task.completed ? 100 : 0,
            month: new Date(task.createdDate).getMonth() + 1,
            monthName: new Date(task.createdDate).toLocaleString("default", { month: "long" }),
          };
        });
        setTasks(formattedTasks);
      } else {
        setTasks([]);
        setSnackbarState({
          open: true,
          message: "No tasks found for the selected filters.",
            severity: "warning",
        });
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
      setSnackbarState({
        open: true,
        message: "Failed to fetch tasks. Please try again.",
        severity: "error",
      });
      setTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  }, [employeeFilter, monthFilter, weekFilter, users, currentPage, pageSize]);

  const filteredTasks = tasks.filter((task) => {
    const employeeMatch = employeeFilter === "All Employees" || task.employee === employeeFilter;
    const weekMatch = weekFilter === "All Weeks" || task.week === weekFilter;
    const monthMatch = monthFilter === "All Months" || task.monthName === monthFilter;
    return employeeMatch && weekMatch && monthMatch;
  });

  const tableData = filteredTasks.map((task) => {
    return {
      taskId: task.id,
      employee: task.employee,
      week: task.week,
      storyPoints: task.storyPoints,
      bonusStatus: task.bonusStatus,
      eligibilityColor: task.bonusStatus === "On Time Bonus" ? "#2e7d32" : "#d32f2f",
    };
  });

  const teamSummary = {
    totalMembers: users.length,
    bonusEligiblePercentage:
      tableData.length > 0
        ? (tableData.filter((task) => task.bonusStatus === "On Time Bonus").length / tableData.length) * 100
        : 0,
  };

  const handleSendReport = async () => {
    setLoading(true);
    setSnackbarState({ open: false, message: "", severity: "" });
    try {
      await sendMetricsReportsToManagers();
      setSnackbarState({
        open: true,
        message: "Metrics report sent successfully!",
        severity: "success",
      });
    } catch (err) {
      console.error("Failed to send report:", err);
      setSnackbarState({
        open: true,
        message: "Failed to send report. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarState({ ...snackbarState, open: false });
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Presentation-only helper
  const bonusChip = (status) => (
    <PillChip label={status} tone={status === "On Time Bonus" ? "success" : "danger"} />
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Box sx={{ 
        maxWidth: '1200px', 
        mx: 'auto', 
        width: '100%', 
        flexGrow: 1, 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' } 
      }}>
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <Sidebar />
        </Box>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minWidth: 0,
            p: { xs: 2, md: 4 },
            pt: { xs: '50px', sm: '60px', md: '120px' },
            backgroundColor: '#fff',
            minHeight: '100vh',
            ml: { md: '20px' },
            pr: { xs: 1, md: 3 },
            position: 'relative',
          }}
        >
          <BackButton />

          <PageHeader title="Manager Team Metrics" />

          <StatGrid>
            <StatCard
              label="Total team members"
              value={teamSummary.totalMembers.toLocaleString()}
              icon={GroupsOutlinedIcon}
            />
            <StatCard
              label="Bonus eligible tasks"
              value={`${teamSummary.bonusEligiblePercentage.toFixed(0)}%`}
              icon={EmojiEventsOutlinedIcon}
              tone="success"
            />
            <StatCard
              label="Tasks shown"
              value={tableData.length.toLocaleString()}
              icon={AssignmentOutlinedIcon}
              tone="warning"
            />
          </StatGrid>

          {/* ------------------------------------------------------ */}
          {/* Filters + report                                       */}
          {/* ------------------------------------------------------ */}
          <SectionCard
            title="Team overview"
            action={
              <Button
                variant="contained"
                onClick={handleSendReport}
                disabled={loading}
                startIcon={
                  loading ? (
                    <CircularProgress size={18} sx={{ color: "inherit" }} />
                  ) : (
                    <SendOutlinedIcon fontSize="small" />
                  )
                }
                sx={primaryButtonSx}
              >
                {loading ? "Sending..." : "Send Report to Managers"}
              </Button>
            }
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  sm: "repeat(2, minmax(0, 1fr))",
                  md: "repeat(3, minmax(0, 1fr))",
                },
                gap: 2.5,
              }}
            >
              <Autocomplete
                options={months}
                value={monthFilter}
                onChange={(event, newValue) => {
                  setMonthFilter(newValue || "All Months");
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Filter by Month" sx={fieldSx} />
                )}
                ListboxProps={{
                  style: {
                    maxHeight: 200,
                    fontSize: '0.9rem'
                  }
                }}
              />
              <Autocomplete
                options={weeks}
                value={weekFilter}
                onChange={(event, newValue) => {
                  setWeekFilter(newValue || "All Weeks");
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Filter by Week" sx={fieldSx} />
                )}
                ListboxProps={{
                  style: {
                    maxHeight: 200,
                    fontSize: '0.9rem'
                  }
                }}
              />
              <Autocomplete
                options={["All Employees", ...users.map(user => user.name)]}
                value={employeeFilter}
                onChange={(event, newValue) => {
                  setEmployeeFilter(newValue || "All Employees");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter by Employee"
                    sx={fieldSx}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                ListboxProps={{
                  style: {
                    maxHeight: 200,
                    fontSize: '0.9rem'
                  }
                }}
              />

              <Box
                sx={{
                  gridColumn: "1 / -1",
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "stretch", sm: "center" },
                  justifyContent: "flex-end",
                  gap: 2,
                  pt: 2.5,
                  borderTop: `1px solid ${ui.border}`,
                }}
              >
                <Button
                  variant="contained"
                  onClick={fetchTasks}
                  disabled={loadingTasks}
                  startIcon={
                    loadingTasks ? <CircularProgress size={18} sx={{ color: "inherit" }} /> : null
                  }
                  sx={primaryButtonSx}
                >
                  {loadingTasks ? "Loading..." : "Fetch tasks"}
                </Button>
              </Box>
            </Box>
          </SectionCard>

          {/* ------------------------------------------------------ */}
          {/* Tasks                                                  */}
          {/* ------------------------------------------------------ */}
          <SectionCard title="Team tasks">
            {loadingTasks ? (
              <ListSkeleton rows={5} />
            ) : tableData.length === 0 ? (
              <EmptyState
                title="No tasks found"
                message="No tasks match the selected filters."
              />
            ) : (
              <>
                {/* Mobile / tablet cards */}
                <Box
                  sx={{
                    display: { xs: "flex", md: "none" },
                    flexDirection: "column",
                    gap: 1.5,
                  }}
                >
                  {tableData.map((row, index) => (
                    <Box key={index} sx={mobileCardSx}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          gap: 1,
                          mb: 1.5,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 700, color: ui.text, wordBreak: "break-word" }}>
                            {row.employee}
                          </Typography>
                          <Typography variant="body2" sx={{ color: ui.muted, wordBreak: "break-word" }}>
                            Task ID: {row.taskId}
                          </Typography>
                        </Box>
                        {bonusChip(row.bonusStatus)}
                      </Box>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                          gap: 1,
                          p: 1.5,
                          borderRadius: "10px",
                          backgroundColor: ui.surface,
                        }}
                      >
                        <Box>
                          <Typography variant="caption" sx={{ color: ui.muted }}>
                            Week
                          </Typography>
                          <Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>{row.week}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: ui.muted }}>
                            Story points
                          </Typography>
                          <Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                            {row.storyPoints ?? "N/A"}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>

                {/* Desktop table */}
                <Box
                  sx={{
                    display: { xs: "none", md: "block" },
                    overflowX: "auto",
                    border: `1px solid ${ui.border}`,
                    borderRadius: "12px",
                  }}
                >
                  <Table aria-label="team metrics table" sx={{ minWidth: 560 }}>
                    <TableHead>
                      <TableRow>
                        {["Task ID", "Employee", "Week", "Story Points", "Bonus Status"].map((header) => (
                          <TableCell key={header} sx={headCellSx}>
                            {header}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tableData.map((row, index) => (
                        <TableRow
                          key={index}
                          hover
                          sx={{ "&:last-child td": { borderBottom: 0 } }}
                        >
                          <TableCell sx={{ ...bodyCellSx, wordBreak: "break-word" }}>{row.taskId}</TableCell>
                          <TableCell sx={{ ...bodyCellSx, fontWeight: 600, wordBreak: "break-word" }}>
                            {row.employee}
                          </TableCell>
                          <TableCell sx={bodyCellSx}>{row.week}</TableCell>
                          <TableCell sx={bodyCellSx}>{row.storyPoints ?? "N/A"}</TableCell>
                          <TableCell sx={bodyCellSx}>{bonusChip(row.bonusStatus)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </>
            )}

            {!loadingTasks && (
              <PagerBar
                currentPage={currentPage}
                totalPages={totalPages}
                onPrev={() => handlePageChange(currentPage - 1)}
                onNext={() => handlePageChange(currentPage + 1)}
              />
            )}
          </SectionCard>
        </Box>
      </Box>
      <Snackbar
        open={snackbarState.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarState.severity}
          variant="filled"
          sx={{ width: "100%", borderRadius: "10px", fontSize: { xs: "0.8rem", sm: "0.9rem" } }}
        >
          {snackbarState.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ViewMetrics;